'use client';

import {
  InputGroup,
  FormControl,
  HStack,
  Text,
  Spinner,
} from '@chakra-ui/react';
import { AsyncSelect } from 'chakra-react-select';
import { claimInputStyles } from '~/theme/autocomplete';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import debounce from 'debounce-promise';
import { SearchIcon } from './autocomplete/SearchIcon';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TODO = any;

// Google Places API types (new Place API)
interface PlacePredictionItem {
  placeId: string;
  text: { text: string };
  mainText?: { text: string };
  secondaryText?: { text: string };
  types: string[];
  toPlace: () => TODO;
}

interface AddressComponent {
  longText?: string;
  shortText?: string;
  types: string[];
}

export interface GoogleAddressResult {
  placeId: string;
  formattedAddress: string;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  country: string;
  latitude: number;
  longitude: number;
}

interface GoogleAddressOption {
  value: string;
  label: React.ReactNode;
  prediction: PlacePredictionItem;
}

const getAddressOption = (
  prediction: PlacePredictionItem
): GoogleAddressOption => {
  return {
    value: prediction.placeId,
    label: (
      <HStack spacing="2">
        <Text fontWeight="bold">
          {prediction.mainText?.text || prediction.text.text}
        </Text>
        {prediction.secondaryText?.text && (
          <Text fontWeight="normal" color="muted">
            {prediction.secondaryText.text}
          </Text>
        )}
      </HStack>
    ),
    prediction,
  };
};

// Parse address components from Google Places API response (new API uses longText/shortText)
const parseAddressComponents = (
  components: AddressComponent[]
): Partial<GoogleAddressResult> => {
  const result: Partial<GoogleAddressResult> = {};

  for (const component of components) {
    const types = component.types;

    if (types.includes('street_number')) {
      result.streetNumber = component.longText || '';
    } else if (types.includes('route')) {
      result.street = component.longText || '';
    } else if (types.includes('postal_town') || types.includes('locality')) {
      result.city = component.longText || '';
    } else if (types.includes('postal_code')) {
      result.postalCode = component.longText || '';
    } else if (types.includes('country')) {
      result.country = component.longText || '';
    }
  }

  return result;
};

export const GoogleAddressAutocomplete = ({
  onChange,
  isLoading,
  disabled,
  value,
  placeholder = 'Sök adress...',
  initialSearchText,
  onInitialSearchTextConsumed,
  originLat,
  originLng,
}: {
  onChange: (result: GoogleAddressResult | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
  value?: GoogleAddressOption | null;
  placeholder?: string;
  initialSearchText?: string;
  onInitialSearchTextConsumed?: () => void;
  /** Optional origin coordinates for distance-weighted ranking (matches Google Maps ordering) */
  originLat?: number;
  originLng?: number;
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [defaultOptions, setDefaultOptions] = useState<GoogleAddressOption[]>([]);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const sessionTokenRef = useRef<TODO>(null);

  // Pre-populate input from the initialSearchText prop.
  // Fetches Google results first, then shows input — avoids a flash of "no results".
  useEffect(() => {
    if (isGoogleLoaded && initialSearchText) {
      fetchPredictions(initialSearchText).then((opts) => {
        setDefaultOptions(opts);
        setInputValue(initialSearchText);
        onInitialSearchTextConsumed?.();
      });
    }
  }, [isGoogleLoaded, initialSearchText]);

  // Rate limiting: sliding window + session cap
  const requestTimestamps = useRef<number[]>([]);
  const totalRequests = useRef(0);
  const RATE_WINDOW_MS = 60_000; // 1 minute window
  const MAX_REQUESTS_PER_WINDOW = 30;
  const MAX_SESSION_REQUESTS = 200;

  const isRateLimited = useCallback(() => {
    const now = Date.now();
    // Prune old timestamps outside the window
    requestTimestamps.current = requestTimestamps.current.filter(
      (t) => now - t < RATE_WINDOW_MS
    );
    if (totalRequests.current >= MAX_SESSION_REQUESTS) return true;
    if (requestTimestamps.current.length >= MAX_REQUESTS_PER_WINDOW)
      return true;
    return false;
  }, []);

  const trackRequest = useCallback(() => {
    requestTimestamps.current.push(Date.now());
    totalRequests.current += 1;
  }, []);

  // Load Google Maps script
  useEffect(() => {
    const loadGoogleMaps = () => {
      // Check if already loaded
      if (window.google?.maps?.places) {
        setIsGoogleLoaded(true);
        return;
      }

      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY;
      if (!apiKey) {
        console.error('Google API key not found');
        return;
      }

      // Check if script is already being loaded or loaded
      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com"]'
      );
      if (existingScript) {
        // Script may already be loaded — check again after a tick
        if (window.google?.maps?.places) {
          setIsGoogleLoaded(true);
        } else {
          existingScript.addEventListener('load', () =>
            setIsGoogleLoaded(true)
          );
        }
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=sv&region=SE&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => setIsGoogleLoaded(true);
      script.onerror = () => console.error('Failed to load Google Maps script');
      document.head.appendChild(script);
    };

    loadGoogleMaps();
  }, []);

  // Create a new session token when Google Maps is loaded
  useEffect(() => {
    if (isGoogleLoaded && window.google?.maps?.places) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [isGoogleLoaded]);

  const fetchPredictions = useCallback(
    async (inputValue: string): Promise<GoogleAddressOption[]> => {
      if (!isGoogleLoaded || inputValue.length < 3) {
        return [];
      }

      if (isRateLimited()) {
        console.warn(
          '[GoogleAddressAutocomplete] Rate limited — too many requests'
        );
        return [];
      }

      try {
        // Use correct primary types for the new Places API (not the legacy 'geocode' collection type).
        // street_address and route cover numbered addresses and street names respectively;
        // premise/subpremise cover buildings and sub-units.
        // Omitting includedPrimaryTypes entirely would return all types but lets Google rank
        // by its own relevance model — we keep the explicit list to filter non-address results.
        // When the query ends with a digit (e.g. "Ängen 6"), Google treats
        // the number as still being typed and returns route-level results.
        // Appending a space signals the number is complete, which causes Google
        // to return street_address results immediately — without requiring the
        // user to type a trailing space themselves.
        const apiInput = /\d$/.test(inputValue.trimEnd())
          ? inputValue.trimEnd() + ' '
          : inputValue;

        const request: TODO = {
          input: apiInput,
          includedRegionCodes: ['se'],
          includedPrimaryTypes: ['street_address', 'route', 'premise', 'subpremise'],
          language: 'sv',
          sessionToken: sessionTokenRef.current,
        };

        // Providing an origin point enables Google's distance-weighted ranking,
        // matching the order seen in Google Maps. Only set when a real location is
        // known (e.g. the home's coordinates) — never fall back to a synthetic
        // Sweden-center point, as that would skew results for users in Norrland.
        if (originLat != null && originLng != null) {
          request.origin = { lat: originLat, lng: originLng };
          // Only bias toward the specific location when we have real coordinates.
          // A tight radius means the nearest match ranks first, matching Google Maps.
          // Without a real origin we skip locationBias entirely and let Google rank
          // within Sweden (includedRegionCodes) using its own relevance model.
          request.locationBias = {
            circle: {
              center: { lat: originLat, lng: originLng },
              radius: 50_000, // 50 km around the known location
            },
          };
        }

        const { suggestions } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request
          );

        trackRequest();

        return (suggestions || [])
          .filter((s: TODO) => s.placePrediction)
          .map((s: TODO) => getAddressOption(s.placePrediction));
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);
        return [];
      }
    },
    [isGoogleLoaded, isRateLimited, trackRequest, originLat, originLng]
  );

  const debouncedFetchPredictions = useMemo(
    () => debounce(fetchPredictions, 300),
    [fetchPredictions]
  );

  const promiseOptions = (inputValue: string) => {
    return debouncedFetchPredictions(inputValue);
  };

  const fetchPlaceDetails = useCallback(
    async (
      prediction: PlacePredictionItem
    ): Promise<GoogleAddressResult | null> => {
      if (!isGoogleLoaded) {
        return null;
      }

      try {
        const place = prediction.toPlace();
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress', 'location'],
        });

        const parsed = parseAddressComponents(place.addressComponents || []);

        // Create a fresh session token for the next session
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();

        return {
          placeId: prediction.placeId,
          formattedAddress: place.formattedAddress || '',
          street: parsed.street || '',
          streetNumber: parsed.streetNumber || '',
          city: parsed.city || '',
          postalCode: parsed.postalCode || '',
          country: parsed.country || 'Sweden',
          latitude: place.location?.lat() || 0,
          longitude: place.location?.lng() || 0,
        };
      } catch (error) {
        console.error('Error fetching place details:', error);
        return null;
      }
    },
    [isGoogleLoaded]
  );

  const handleChange = async (option: GoogleAddressOption | null) => {
    if (!option) {
      onChange(null);
      return;
    }

    // Fetch full place details using the prediction's toPlace() method
    const details = await fetchPlaceDetails(option.prediction);

    if (!details) {
      // fetchPlaceDetails failed — don't silently clear the address
      console.warn(
        '[GoogleAddressAutocomplete] Place details fetch failed, ignoring selection'
      );
      return;
    }

    // If Google didn't return a route component, try to extract street from prediction text
    if (!details.street && option.prediction.mainText?.text) {
      // mainText is usually "Storgatan 5" — extract the street name (without number)
      const mainText = option.prediction.mainText.text;
      const streetMatch = mainText.match(/^(.+?)\s+\d/);
      details.street = streetMatch ? streetMatch[1].trim() : mainText;
    }

    onChange(details);
  };

  return (
      <InputGroup
        width="100%"
        minWidth="350px"
        position="relative"
        zIndex="10000"
      >
        <FormControl
          id="react-select-google-address-input"
          cursor="text"
          zIndex={10000}
          p={0}
          width="100%"
          position="relative"
        >
          <SearchIcon isLoading={isLoading || !isGoogleLoaded} />
          <AsyncSelect
            inputValue={inputValue}
            onInputChange={(v, { action }) => {
              if (action === 'input-change') setInputValue(v);
              if (action === 'set-value' || action === 'menu-close') setInputValue('');
            }}
            menuIsOpen={inputValue.length > 0}
            cacheOptions
            defaultOptions={defaultOptions}
            loadOptions={promiseOptions as TODO}
            isClearable
            isDisabled={disabled || !isGoogleLoaded}
            autoFocus
            instanceId="google-address"
            closeMenuOnSelect={true}
            name="google-address"
            placeholder={isGoogleLoaded ? placeholder : 'Laddar...'}
            value={value as TODO}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Escape') {
                onChange(null);
              }
            }}
            onChange={handleChange as TODO}
            loadingMessage={({ inputValue }: { inputValue: string }) =>
              inputValue && inputValue.length > 2 ? (
                <HStack px="5">
                  <Text fontSize="md" fontWeight="bold">
                    Söker adresser...
                  </Text>
                  <Spinner size="xs" />
                </HStack>
              ) : null
            }
            noOptionsMessage={({ inputValue }: { inputValue: string }) => {
              if (inputValue.length < 3) {
                return (
                  <Text fontSize="md" color="gray.500">
                    Skriv minst 3 tecken för att söka
                  </Text>
                );
              }
              return (
                <Text fontSize="md" fontWeight="bold">
                  Inga adresser hittades
                </Text>
              );
            }}
            size="lg"
            tagColorScheme="brand"
            tagVariant="solid"
            selectedOptionStyle="check"
            selectedOptionColorScheme="brand"
            variant="outline"
            chakraStyles={claimInputStyles}
            menuPortalTarget={
              typeof window !== 'undefined' ? document.body : undefined
            }
            menuPosition="fixed"
            styles={{
              menuPortal: (base: any) => ({ ...base, zIndex: 10000 }),
            }}
          />
        </FormControl>
      </InputGroup>
  );
};
